/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * This component is used to render the User Prompt Input steps
 * This includes PromptInput and MultiTurnContinue steps
 */
import { Tooltip, TextArea } from '@scale/scaleui';
import { Button, Callout, Icon } from '@scale/scaleui-radix';
import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  ReactNode,
  useRef,
  useMemo,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AutoCritiqueButton, AutoCritiqueAlert, LaTeXEditor } from './TempComponents';
import { useTrans } from './hooks/useTrans';
import useAutoTyperDetector from './hooks/useAutoTyperDetector';
import { useAttemptBehaviorLog } from './contexts/AttemptBehaviorLogContext';
import {
  ReferenceText,
  Attachment,
  InteractionStepType,
  AutoFeedbackChatResponse,
  MessageRole,
} from '@scale/shared/types/chat';
import ReferenceTextGroup from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/ReferenceTextGroup';
import AttachmentGroup from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/AttachmentGroup';
import WordCounter from '../common/WordCounter';
import UtilitiesWrapper from '../common/UtilitiesWrapper';
import { ChatTaskContext } from './contexts/ChatTaskContext';
import useAutoCritique from './hooks/useAutoCritique';
import { useChatLayoutStore } from './hooks/useChatLayoutStore';
import {
  useChatStoreExtract,
  useCurrentStep,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { StepCard } from './step/StepCard';
import useAutoFeedback from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/hooks/useAutoFeedback';
import AutoFeedback from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback';
import { AutoFeedbackProps } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/types';
import { useMultimodalImageService } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMultimodalImageService';
import useMountTime from 'react-scale/hooks/useMountTime';
import {
  Editor,
  EditorContainer,
  FooterContainer,
  PlainTextEditorContext,
  WordCounter as EditorWordCounter,
  KeyHandler,
} from './components/editor';
import MessageRoleSelect from './components/MessageRoleSelect';
import { getContentSerializer } from './components/editor/plugins/Content';
import useLatest from './hooks/useLatest';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

interface InteractionInputProps {
  onSubmit?: (
    response: string,
    autoCritiqueResponse: string,
    autoFeedbackResponse?: AutoFeedbackChatResponse,
    skipPromptInput?: boolean,
  ) => Promise<boolean>;
  placeholder?: string;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  referenceTexts: ReferenceText[];
  role?: MessageRole;
  setRole?: (role: MessageRole) => void;
  setReferenceTexts: (referenceTexts: ReferenceText[]) => void;
  modelPromptSelector?: ReactNode;
  selectedPrompt?: string;
  setSelectedPrompt?: Function;
  displaySuggestionText?: boolean;
  responseIndex?: number;
  attachments?: Attachment[];
  setAttachments: (attachments: Attachment[]) => void;
  enableRTLText?: boolean;
  autoCritiqueResponse?: string;
  autoFeedbackProps?: AutoFeedbackProps;
  isMultiTurnStep: boolean;
  initialPromptText?: string;
}

interface InputData {
  text: string;
}

// eslint-disable-next-line
export const InteractionInput: React.FC<InteractionInputProps> = ({
  title,
  subtitle,
  onSubmit,
  placeholder = 'Add your prompt',
  actions,
  referenceTexts,
  setReferenceTexts,
  modelPromptSelector,
  selectedPrompt,
  setSelectedPrompt,
  displaySuggestionText,
  responseIndex,
  attachments,
  setAttachments,
  enableRTLText,
  autoCritiqueResponse,
  autoFeedbackProps,
  isMultiTurnStep,
  initialPromptText,
  role,
  setRole,
}) => {
  const taskContext = useContext(ChatTaskContext);
  // NOTE: We need to keep track whether the component is mounted or not, because we have an async callback which will update the state.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const { emitTypingEvent, incAutotyperDetectedEvent, incMouseLeaveEvent } =
    useAttemptBehaviorLog();
  const { t } = useTrans();
  const { handleSubmit, reset, control, setValue, watch } = useForm<InputData>({
    defaultValues: { text: initialPromptText ?? '' },
  });
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [error, setError] = useState<{
    message: string;
    severity: 'warning' | 'error';
  } | null>(null);

  const step = useCurrentStep();

  const enableMathPlugin = !!taskContext.milkdownEditorConfig?.enableMathPlugin;

  const autoCritiqueHook = useAutoCritique(autoCritiqueResponse);
  const enableAutoCritique = taskContext.autoCritiqueConfig?.prompt?.enabled;
  const autoCritiqueLabel = taskContext.autoCritiqueConfig?.prompt?.renameButton;

  const config = useChatStoreExtract(state => state.config);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const currentMultiChatIndex = useChatStoreExtract(state => state.currentMultiChatIndex);
  const hasDrawerBeenOpened = useChatLayoutStore(state => state.hasDrawerBeenOpened);
  const setIsDrawerOpen = useChatLayoutStore(state => state.setIsDrawerOpen);

  const startTime = useMountTime();
  const multimodalImageServiceHook = useMultimodalImageService({
    projectId: taskContext.projectId,
    assignmentId: taskContext.assignmentId,
    configVariables: config.templateVariables ?? {},
    reviewLevel,
    startTime,
  });

  const {
    minReferenceTexts,
    maxReferenceTexts,
    minReferenceTextsLength,
    maxReferenceTextsLength,
    hideReferenceURL,
    showReferenceCategory,
    allowSelectPdfAttachmentAsReferenceText,
    referenceTextCategories,
    minAttachments,
    maxAttachments,
    isReferenceTextReadOnly,
  } = config;
  const enableAttachments =
    config.useAttachments && (!isMultiTurnStep || (config.useAttachmentsOnMultiTurn ?? true));
  const enableImageUrls =
    config.useImageUrls && (!isMultiTurnStep || config.useImageUrlsOnMultiTurn);
  const enableMmImageService =
    config.enableMmImageService && (!isMultiTurnStep || config.useImageUrlsOnMultiTurn);
  const isAttachmentRequired =
    config.isAttachmentRequired ||
    (config.isImageUrlRequired && !(isMultiTurnStep && config.requireImageUrlsOnlySingleTurn));
  // default useReferenceTextOnMultiTurn to true if not defined to maintain backwards compatibility
  const enableReferenceText =
    config.useReferenceText && (!isMultiTurnStep || (config.useReferenceTextOnMultiTurn ?? true));
  const isReferenceTextRequired =
    config.isReferenceTextRequired &&
    !(isMultiTurnStep && config.requireReferenceTextOnlySingleTurn);
  const {
    disablePromptPasting: disablePasting,
    disableAutoTyperTextRemoval,
    enableWordCounter,
  } = taskContext;

  const { showMessageRoleSelector } = useMemo(() => {
    return { showMessageRoleSelector: config.showMessageRoles && role };
  }, [role, config.showMessageRoles]);

  const { showSkipPromptInputButton } = useMemo(() => {
    return { showSkipPromptInputButton: config.enableSkipPromptInput && isMultiTurnStep };
  }, [config.enableSkipPromptInput, isMultiTurnStep]);

  const handleFetchAutoCritique = useCallback(
    async (fields: InputData) => {
      const value = selectedPrompt || fields.text;
      if (value) {
        await autoCritiqueHook.getAutoCritique({
          scope: 'prompt',
          projectId: taskContext.projectId,
          taskId: taskContext.taskId,
          assignmentId: taskContext.assignmentId,
          workerId: taskContext.workerId,
          variables: {
            ...config.templateVariables,
            prompt: value,
          },
        });
      }
    },
    [
      selectedPrompt,
      autoCritiqueHook,
      taskContext.projectId,
      taskContext.taskId,
      taskContext.assignmentId,
      taskContext.workerId,
      config.templateVariables,
    ],
  );

  const autoFeedbackHook = useAutoFeedback({
    id: autoFeedbackProps?.childId ?? '',
    responseIndex,
    reviewLevel,
    stepType: InteractionStepType.PromptInput,
    stepId: step?.id,
    multichatIndex: currentMultiChatIndex,
  });

  const shouldUseAutoFeedback =
    !taskContext.enableUnifiedFeedback &&
    !!autoFeedbackProps?.childId &&
    !autoFeedbackHook?.isFetchingStepCompatible &&
    (autoFeedbackHook?.compatibleSteps.includes(InteractionStepType.PromptInput) ||
      autoFeedbackHook?.compatibleSteps.includes(step?.id ?? ''));

  const submitTooltip = autoCritiqueHook.critiqueData.loading
    ? 'Critique is loading...'
    : shouldUseAutoFeedback && !autoFeedbackHook?.error && !autoFeedbackHook?.feedback // Ensures that contributors run auto-feedback at least once before submitting
    ? 'Please run the AutoFeedback system by pressing "Get Feedback" before submitting'
    : '';

  const onDetectAutoTyper = useCallback(() => {
    incAutotyperDetectedEvent({
      fieldId: 'promptText',
      responseIndex,
    });
    if (disablePasting && !disableAutoTyperTextRemoval) {
      reset();
      setShowErrorMessage(true);
      setError({ message: 'This field does not allow pasting.', severity: 'warning' });
    }
  }, [
    reset,
    disablePasting,
    disableAutoTyperTextRemoval,
    incAutotyperDetectedEvent,
    responseIndex,
  ]);

  const autoTyperDetectorHook = useAutoTyperDetector(onDetectAutoTyper);

  useEffect(() => {
    return reset();
  }, [reset]);

  // Either of the following should be enabled for attachment validation
  const attachmentsEnabledOnStep = useMemo(
    () => enableAttachments || enableImageUrls || enableMmImageService,
    [enableAttachments, enableImageUrls, enableMmImageService],
  );

  const validateAndSubmitPrompt = useCallback(
    // eslint-disable-next-line complexity
    async (
      prompt: string,
      critiqueResponse: string,
      autoFeedbackResponse: AutoFeedbackChatResponse,
      skipPromptInput: boolean = false,
    ) => {
      if (!prompt && !skipPromptInput) {
        setShowErrorMessage(true);
        setError({ message: 'Please add a prompt.', severity: 'warning' });
        return;
      }
      if (enableReferenceText && isReferenceTextRequired && referenceTexts.length === 0) {
        setShowErrorMessage(true);
        setError({
          message: 'Please add Reference Texts by pressing the "Add" icon.',
          severity: 'warning',
        });
        return;
      }
      if (
        enableReferenceText &&
        isReferenceTextRequired &&
        minReferenceTexts &&
        minReferenceTexts > referenceTexts.length
      ) {
        setShowErrorMessage(true);
        setError({
          message: `Please add at least ${minReferenceTexts} Reference Texts.`,
          severity: 'warning',
        });
        return;
      }
      if (enableReferenceText && maxReferenceTexts && maxReferenceTexts < referenceTexts.length) {
        setShowErrorMessage(true);
        setError({
          message: `Please add at most ${maxReferenceTexts} Reference Texts.`,
          severity: 'warning',
        });
        return;
      }
      if (
        enableReferenceText &&
        minReferenceTextsLength &&
        referenceTexts.some(
          referenceText => (referenceText.content?.length ?? 0) < minReferenceTextsLength,
        )
      ) {
        setShowErrorMessage(true);
        setError({
          message: `Please make sure the reference text is at least ${minReferenceTextsLength} characters long.`,
          severity: 'warning',
        });
        return;
      }
      if (
        enableReferenceText &&
        maxReferenceTextsLength &&
        referenceTexts.some(
          referenceText => (referenceText.content?.length ?? 0) > maxReferenceTextsLength,
        )
      ) {
        setShowErrorMessage(true);
        setError({
          message: `Please make sure the reference text is at most ${maxReferenceTextsLength} characters long.`,
          severity: 'warning',
        });
        return;
      }
      if (
        enableReferenceText &&
        showReferenceCategory &&
        referenceTexts.some(referenceText => !referenceText.category)
      ) {
        setShowErrorMessage(true);
        setError({
          message: 'Please select a category for each reference text.',
          severity: 'warning',
        });
        return;
      }
      if (attachmentsEnabledOnStep && isAttachmentRequired && attachments?.length === 0) {
        setShowErrorMessage(true);
        setError({
          message: "Please add Attachments by pressing the 'File' icon.",
          severity: 'warning',
        });
        return;
      }
      if (
        attachmentsEnabledOnStep &&
        minAttachments &&
        minAttachments > (attachments?.length ?? 0)
      ) {
        setShowErrorMessage(true);
        setError({
          message: `Please add at least ${minAttachments} Attachments.`,
          severity: 'warning',
        });
        return;
      }
      if (
        attachmentsEnabledOnStep &&
        maxAttachments &&
        maxAttachments < (attachments?.length ?? 0)
      ) {
        setShowErrorMessage(true);
        setError({
          message: `Please add at most ${maxAttachments} Attachments.`,
          severity: 'warning',
        });
        return;
      }
      if (onSubmit) {
        const isValid = await onSubmit(
          prompt,
          critiqueResponse,
          autoFeedbackResponse,
          skipPromptInput,
        );
        if (!isValid) {
          return;
        }
        if (config.enableMmImageService && attachments && taskContext.projectId) {
          for (const attachment of attachments) {
            multimodalImageServiceHook.logPromptInputEvent(
              taskContext.projectId,
              attachment,
              startTime,
              taskContext?.assignmentId,
            );
          }
        }
        if (!mounted.current) return;
        if (!hasDrawerBeenOpened) {
          setIsDrawerOpen(true);
        }
        setShowErrorMessage(false);
        setError(null);
        reset();
      }
    },
    [
      isReferenceTextRequired,
      minReferenceTexts,
      maxReferenceTexts,
      minReferenceTextsLength,
      maxReferenceTextsLength,
      isAttachmentRequired,
      minAttachments,
      maxAttachments,
      attachments,
      attachmentsEnabledOnStep,
      onSubmit,
      referenceTexts,
      reset,
      enableReferenceText,
      hasDrawerBeenOpened,
      setIsDrawerOpen,
    ],
  );

  const validateAndSubmitPromptRef = useLatest(validateAndSubmitPrompt);

  const onKeyDownSubmit = useCallback(
    (e: React.KeyboardEvent) => {
      // Catch shift + enter to submit by catching default behavior
      if (e.shiftKey && e.key === 'Enter') {
        // submit on shift+enter
        e.preventDefault();
        const value = selectedPrompt || (e.target as HTMLInputElement).value.trim();
        if (value) {
          validateAndSubmitPrompt(value, autoCritiqueHook.critiqueData.critique, {
            feedback: autoFeedbackHook?.feedback ?? [],
          });
        }
        return;
      }
    },
    [validateAndSubmitPrompt, selectedPrompt, autoCritiqueHook, autoFeedbackHook?.feedback],
  );

  const handleSubmitPrompt = useCallback(
    (fields: InputData) => {
      const value = selectedPrompt || fields.text;
      validateAndSubmitPrompt(value, autoCritiqueHook.critiqueData.critique, {
        feedback: autoFeedbackHook?.feedback ?? [],
      });
    },
    [validateAndSubmitPrompt, selectedPrompt, autoCritiqueHook, autoFeedbackHook?.feedback],
  );

  const handleOnMouseLeave = useCallback(() => {
    incMouseLeaveEvent({
      fieldId: 'promptText',
      responseIndex,
    });
  }, [incMouseLeaveEvent, responseIndex]);

  return (
    <StepCard>
      {(title || subtitle || displaySuggestionText) && (
        <StepCard.Header>
          <StepCard.HeaderLeft>
            <StepCard.Icon icon="comment" />
            <StepCard.HeaderColumn>
              {title && <StepCard.Title>{title}</StepCard.Title>}
              {subtitle && <StepCard.Subtitle>{subtitle}</StepCard.Subtitle>}
              {displaySuggestionText && <StepCard.Subtitle>{t('suggestions')}</StepCard.Subtitle>}
            </StepCard.HeaderColumn>
          </StepCard.HeaderLeft>
        </StepCard.Header>
      )}
      <StepCard.Body className="pt-3">
        {showMessageRoleSelector && (
          <div className="mb-4">
            <MessageRoleSelect role={role} setRole={setRole} />
          </div>
        )}
        <div className="mb-4">{modelPromptSelector}</div>
        <form>
          <Controller
            name="text"
            control={control}
            render={({ field: { onChange, value } }) => {
              const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setShowErrorMessage(false);
                setError(null);
                if (setSelectedPrompt) {
                  setSelectedPrompt('');
                }
                emitTypingEvent({
                  fieldId: 'promptText',
                  text: e.target.value,
                  responseIndex,
                });
                autoTyperDetectorHook.updateKeystrokeTimestamp();
                const textArea = e.target;
                textArea.style.height = 'auto';
                textArea.style.height = textArea.scrollHeight + 'px';
                return onChange(e);
              };
              return (
                <div className="flex flex-1 flex-col">
                  <div
                    className={`${
                      enableMathPlugin ? '' : 'border rounded border-neutral-200'
                    } flex flex-1`}
                  >
                    <UtilitiesWrapper enableRTL={enableRTLText}>
                      {enableMathPlugin ? (
                        <LaTeXEditor
                          initialText={selectedPrompt || value}
                          readOnly={false}
                          placeholder={placeholder}
                          enableSingleDollarTextMath={true}
                        >
                          <TextArea
                            id="text"
                            data-field-id="promptText"
                            placeholder={placeholder}
                            className="flex-1 w-1/2"
                            value={selectedPrompt || value}
                            rows={2}
                            onChange={handleInputChange}
                            onPaste={e => {
                              if (disablePasting) {
                                e.preventDefault();
                                setShowErrorMessage(true);
                                setError({
                                  message: 'This field does not allow pasting.',
                                  severity: 'warning',
                                });
                              }
                            }}
                            onKeyDown={onKeyDownSubmit}
                            onMouseLeave={handleOnMouseLeave}
                            onDragOver={e => {
                              if (disablePasting) {
                                e.preventDefault();
                              }
                            }}
                            onDrop={e => {
                              if (disablePasting) {
                                e.preventDefault();
                                setShowErrorMessage(true);
                                setError({
                                  message: 'This field does not allow dragging and dropping.',
                                  severity: 'warning',
                                });
                              }
                            }}
                            data-testid="math-input-textarea"
                          />
                        </LaTeXEditor>
                      ) : (
                        <PlainTextEditorContext
                          initialContent={selectedPrompt || value}
                          extensions={[
                            KeyHandler.configure({
                              shortcuts: {
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                'Shift-Enter': ({ editor }) => {
                                  const getContent = getContentSerializer(editor);
                                  const value = getContent();
                                  const content = selectedPrompt || value.trim();
                                  if (content) {
                                    validateAndSubmitPromptRef.current(
                                      content,
                                      autoCritiqueHook.critiqueData.critique,
                                      {
                                        feedback: autoFeedbackHook?.feedback ?? [],
                                      },
                                    );
                                  }
                                  return true;
                                },
                              },
                            }),
                          ]}
                        >
                          <EditorContainer className="w-full">
                            <Editor
                              content={selectedPrompt || value}
                              onContentChange={value => {
                                setShowErrorMessage(false);
                                setError(null);
                                if (setSelectedPrompt) {
                                  setSelectedPrompt('');
                                }
                                emitTypingEvent({
                                  fieldId: 'promptText',
                                  text: value,
                                  responseIndex,
                                });
                                autoTyperDetectorHook.updateKeystrokeTimestamp();
                                return onChange(value);
                              }}
                              disablePaste={disablePasting}
                              onDisabledPaste={() => {
                                setShowErrorMessage(true);
                                setError({
                                  message: 'This field does not allow pasting.',
                                  severity: 'warning',
                                });
                              }}
                              onMouseLeave={handleOnMouseLeave}
                              data-testid="interaction-input-editor"
                            />
                            <FooterContainer>
                              <EditorWordCounter />
                            </FooterContainer>
                          </EditorContainer>
                        </PlainTextEditorContext>
                      )}
                    </UtilitiesWrapper>
                  </div>
                  {enableWordCounter && enableMathPlugin && (
                    <div className="py-2">
                      <WordCounter text={selectedPrompt || value} />
                    </div>
                  )}
                  {
                    <AutoCritiqueAlert
                      autoCritiqueHook={autoCritiqueHook}
                      headerLabel={autoCritiqueLabel}
                    />
                  }
                </div>
              );
            }}
          />
        </form>
        {enableReferenceText && (
          <ReferenceTextGroup
            required={isReferenceTextRequired}
            hideUrl={hideReferenceURL}
            showReferenceCategory={showReferenceCategory}
            allowSelectPdfAttachmentAsReferenceText={allowSelectPdfAttachmentAsReferenceText}
            referenceTextCategories={referenceTextCategories}
            referenceTexts={referenceTexts}
            setReferenceTexts={v => {
              setReferenceTexts(v);
              setError(null);
              setShowErrorMessage(false);
            }}
            maxReferenceTexts={maxReferenceTexts}
            minReferenceTextsLength={minReferenceTextsLength}
            maxReferenceTextsLength={maxReferenceTextsLength}
            readOnly={isReferenceTextReadOnly}
          />
        )}
        {(enableAttachments || enableImageUrls || enableMmImageService) && (
          <AttachmentGroup
            attachments={attachments || []}
            setAttachments={v => {
              setAttachments(v);
              setError(null);
              setShowErrorMessage(false);
            }}
            readOnly={config.disableImageEditing}
            enableImageUrls={enableImageUrls}
            enableAttachments={enableAttachments}
            enableMmImageService={enableMmImageService}
            multimodalImageServiceHook={multimodalImageServiceHook}
            appendPromptText={(textToAppend: string) =>
              setValue('text', watch('text') + ' ' + textToAppend)
            }
          />
        )}
        {showErrorMessage && error && (
          <Callout.Root className="mt-5" size="1" color={error.severity}>
            <Callout.Icon>
              <Icon icon={faCircleInfo} label="info" size="1" />
            </Callout.Icon>
            <Callout.Text>{error.message}</Callout.Text>
          </Callout.Root>
        )}
        {shouldUseAutoFeedback && (
          <Controller
            name="text"
            control={control}
            render={({ field }) => {
              return (
                <AutoFeedback
                  autoFeedbackHook={autoFeedbackHook}
                  inputContent={selectedPrompt || field.value}
                  attachments={attachments}
                />
              );
            }}
          />
        )}
      </StepCard.Body>
      <StepCard.Footer className="flex items-center justify-between mt-3">
        <div className="px-2">{t('submit-hint')}</div>
        <div className="flex gap-2">
          {enableAutoCritique && (
            <AutoCritiqueButton
              onClick={handleSubmit(handleFetchAutoCritique)}
              loading={autoCritiqueHook.critiqueData.loading}
              buttonLabel={autoCritiqueLabel}
            />
          )}
          {showSkipPromptInputButton && (
            <Button
              color="violet"
              size="1"
              variant="soft"
              onClick={() => {
                // Submit an empty prompt
                validateAndSubmitPrompt('', '', { feedback: [] }, true);
              }}
            >
              {t('skip-message')}
            </Button>
          )}
          <Tooltip
            target={
              <Button
                data-testid="submit-prompt-button"
                size="1"
                onClick={handleSubmit(handleSubmitPrompt)}
                disabled={!!submitTooltip}
              >
                {t('submit-message')}
              </Button>
            }
            text={submitTooltip}
            side="top"
          />

          {actions}
        </div>
      </StepCard.Footer>
    </StepCard>
  );
};
